import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  constants,
  countMultipleTotalBonusOdds,
  get,
  getConfigProp,
  isEmptyCollection,
  useBetslipState,
  usePersonalBonusBoost,
} from '@atlas/core';
import { BetOdds, FreebetBonusSelect, Label, MoneyValue } from '@atlas/ui';

import { currencyIcons } from 'common/constants';

import MultipleBonusInfo from './MultipleBonusInfo';

const { BET_TYPE } = constants;

const TotalPossibleWin = ({ currencySymbol = '', finalPossibleWin, isSystem }) => {
  if (isSystem || !finalPossibleWin) return null;

  return (
    <div className="total-possible-win">
      <div className="title-text">
        <div className="label">
          <Label message="total_possible_win" />:
        </div>
        <MoneyValue
          className="potential-win-amount"
          currency={currencySymbol}
          value={finalPossibleWin}
        />
      </div>
    </div>
  );
};

const InfoSection = ({
  betAmount = 0,
  currency = '',
  multipleBonuses,
  removeSelectedBonuses,
  selectBonus,
  selectedBet = {},
  selectedFreebet = {},
}) => {
  const isSportRadarProject = getConfigProp('isSportRadarProject');
  const { bonuses, loading } = useBetslipState();
  const isSystem = selectedBet.betType === constants.BET_TYPE.SYSTEM;
  const isMultiple = selectedBet.betType === BET_TYPE.MULTIPLE;
  const currencySymbol = currencyIcons[currency] || '';
  const personalBonusBoost = usePersonalBonusBoost(
    !isSportRadarProject ? bonuses[selectedBet.requestId] || [] : [],
    betAmount,
  );

  const bonusesToPreview = bonuses[selectedBet.requestId];

  const multipleBonus = get(multipleBonuses, [0], {});

  const { freeBetAmount } = selectedFreebet;
  const { odds } = selectedBet;

  const oddsWithPersonalBonus =
    personalBonusBoost && personalBonusBoost.reached
      ? odds * personalBonusBoost.oddsMultiplier
      : odds;

  const finalPossibleWin = useMemo(() => {
    if (freeBetAmount) {
      return freeBetAmount * odds - freeBetAmount;
    }

    if (!isEmptyCollection(multipleBonus)) {
      return countMultipleTotalBonusOdds(oddsWithPersonalBonus, betAmount, multipleBonus);
    }

    return oddsWithPersonalBonus * betAmount;
  }, [betAmount, freeBetAmount, multipleBonus, odds, oddsWithPersonalBonus]);

  const renderTotalOdds = useMemo(() => {
    if (personalBonusBoost && !personalBonusBoost.reached) {
      const minBetAmount = personalBonusBoost.minBetAmount / 100;

      return (
        <div className="personal-bonus-container">
          <div className="personal-bonus-info">
            <div className="personal-bonus-labels">
              <div className="total-odds_label">
                <Label message="total odds" />:
              </div>
              <div className="bet_more_label">
                <Label
                  message="betslip_bet_more"
                  params={{ Amount: `${currencySymbol}${minBetAmount}` }}
                />
                :
              </div>
            </div>
            <div className="personal-bonus-badge">
              <div className="personal-bonus-badge__label">
                <Label message="betslip_personal_odds_boost" />
              </div>
              <div className="personal-bonus-badge__coefficient">
                <BetOdds odds={odds * personalBonusBoost.oddsMultiplier} />
              </div>
            </div>
          </div>
          <div className="bet-under">
            <div className="label">
              <Label message="betslip_bet_under" />
              &nbsp;
              {currencySymbol}
              {minBetAmount}:
            </div>
            <BetOdds className="odd-amount" odds={odds} />
          </div>
        </div>
      );
    }

    if (personalBonusBoost && personalBonusBoost.reached && !freeBetAmount) {
      const personalOddsMultiplier =
        Math.round(personalBonusBoost.oddsMultiplier * 100 - 100) / 100;

      return (
        <div className="personal-bonus-container">
          <div className="title-small-text">
            <div className="label">
              <Label message="odds" />:
            </div>
            <BetOdds className="odd-amount" odds={odds} />
          </div>
          <div className="title-small-text boost-percentage">
            <div className="label">
              + &nbsp;
              <Label
                message="betslip_personal_odds_boost_percentage"
                params={{ Percentage: personalOddsMultiplier * 100 }}
              />
              :
            </div>
            <BetOdds className="odd-amount" odds={odds * personalOddsMultiplier} />
          </div>
          <div className="title-small-text total-odds">
            <div className="label">
              <Label message="total odds" />:
            </div>
            <BetOdds className="odd-amount" odds={odds * personalBonusBoost.oddsMultiplier} />
          </div>
        </div>
      );
    }

    if (isMultiple) {
      return (
        <div className="title-small-text total-odds">
          <div className="label">
            <Label message="total odds" />:
          </div>
          <BetOdds className="odd-amount" odds={odds} />
        </div>
      );
    }

    return null;
  }, [personalBonusBoost, freeBetAmount, isMultiple, currencySymbol, odds]);

  return (
    <div className="total-bet-info">
      {isEmptyCollection(bonusesToPreview) || isSystem || isSportRadarProject ? (
        <div className="total-bet-info-empty" />
      ) : (
        <div className="freebet-container">
          <FreebetBonusSelect
            bonuses={bonusesToPreview}
            currencySymbol={currencySymbol}
            disabled={loading}
            removeSelectedBonuses={removeSelectedBonuses}
            selectBonus={selectBonus}
            selectedFreebet={selectedFreebet}
          />
        </div>
      )}
      {!isSystem && (!!finalPossibleWin || isMultiple || personalBonusBoost) && (
        <div className="bet-info-container">
          {selectedBet && !isSystem ? renderTotalOdds : null}
          <MultipleBonusInfo
            betAmount={betAmount}
            currencySymbol={currencySymbol}
            finalPossibleWin={finalPossibleWin}
            multipleBonus={multipleBonus}
            oddsWithPersonalBonus={oddsWithPersonalBonus}
            selectedBet={selectedBet}
            selectedFreebet={selectedFreebet}
          />

          <TotalPossibleWin
            betAmount={betAmount}
            currencySymbol={currencySymbol}
            finalPossibleWin={finalPossibleWin}
            isSystem={isSystem}
            multipleBonus={multipleBonus}
            selectedBet={selectedBet}
            selectedFreebet={selectedFreebet}
          />
        </div>
      )}
    </div>
  );
};

InfoSection.propTypes = {
  betAmount: PropTypes.number,
  currency: PropTypes.string,
  multipleBonuses: PropTypes.array.isRequired,
  removeSelectedBonuses: PropTypes.func.isRequired,
  selectBonus: PropTypes.func.isRequired,
  selectedBet: PropTypes.object,
  selectedFreebet: PropTypes.object,
};

export default InfoSection;
